import type { ConfigListFetchResult } from "./useConfigListQuery";

export type ConfigListCacheScope = "portal-credentials";

/**
 * Persistent page cache for Config lists.
 * Survives section remounts within the Config SPA visit.
 * Cleared when leaving Config (layout unmount) or on full browser refresh.
 */
const persistentConfigListCache = new Map<
  string,
  ConfigListFetchResult<unknown>
>();

export function configListCacheKey(
  scope: ConfigListCacheScope,
  brandKey: string,
  page: number,
  limit: number,
  search: string,
): string {
  return `${scope}|${brandKey}|${page}|${limit}|${search.trim().toLowerCase()}`;
}

export function getConfigListCacheEntry<TItem>(
  key: string,
): ConfigListFetchResult<TItem> | undefined {
  return persistentConfigListCache.get(key) as
    | ConfigListFetchResult<TItem>
    | undefined;
}

export function setConfigListCacheEntry<TItem>(
  key: string,
  entry: ConfigListFetchResult<TItem>,
): void {
  persistentConfigListCache.set(key, entry as ConfigListFetchResult<unknown>);
}

export function clearConfigListCache(): void {
  persistentConfigListCache.clear();
}

/** Drop cached Config list pages for the given scopes (e.g. after a write). */
export function invalidateConfigListCacheScopes(
  scopes: ConfigListCacheScope[],
): void {
  for (const key of persistentConfigListCache.keys()) {
    if (scopes.some((scope) => key.startsWith(`${scope}|`))) {
      persistentConfigListCache.delete(key);
    }
  }
}
